import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Home } from "lucide-react";
import { NAV_ITEMS, SCHOOL, SLIDES, CAMPUS_DATA, CLASSES } from "../data/siteData";
import logo from "../assets/idps-logo.png";
import "./Header.css";

/* Mega-menu media + description shown for each top-level item that has children */
const MEGA_CONTENT = {
  About: {
    image: SLIDES[0].img,
    desc: "At IDPS Mahore, we believe education is about preparing children for life, not just for school. With this philosophy as our inspiration, we provide a stimulating environment where children enjoy learning and perform to the best of their abilities.",
  },
  Admissions: {
    image: CAMPUS_DATA[4].img,
    desc: "We welcome you and share our enthusiasm as we open the gates of IDPS Mahore. We remain committed to high expectations for every student through an academically challenging program that develops social and emotional needs as well.",
  },
  Academics: {
    image: CLASSES[0].img,
    desc: "Welcome to the new academic session. Education is not about scoring marks — it is about personal growth. At IDPS Mahore, the best student is not the one who scores the maximum marks, but the one who learns every day.",
  },
  Campus: {
    image: CAMPUS_DATA[0].img,
    desc: "IDPS Mahore is dedicated to constantly upgrading its unique & futuristic system of education, one of its kind in the schooling fraternity — a dynamic educational community built by committed faculty and enthusiastic parents.",
  },
};

/* split an array into two roughly-equal columns */
const twoCols = (arr) => {
  const mid = Math.ceil(arr.length / 2);
  return [arr.slice(0, mid), arr.slice(mid)];
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  const closeMobile = () => {
    setMobileOpen(false);
    setOpenDropdown(null);
  };

  return (
    <header className="header">
      <div className="header__inner">
        {/* Logo */}
        <Link to="/" className="header__logo" onClick={closeMobile}>
          <img src={logo} alt="IDPS Mahore Logo" className="header__logo-img" />
          <div className="header__logo-text">
            <h1>{SCHOOL.name}</h1>
            <span>{SCHOOL.short}</span>
          </div>
        </Link>

        {/* Hamburger */}
        <button
          className="hamburger-btn"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Desktop Nav */}
        <nav className="desktop-nav">
          <ul className="nav__list">
            <li className="nav__item">
              <Link to="/" className="nav__link nav__link--home" aria-label="Home">
                <Home size={17} />
              </Link>
            </li>
            {NAV_ITEMS.filter((i) => i.label !== "Home").map((item) => {
              const mega = MEGA_CONTENT[item.label];
              const [col1, col2] = item.children ? twoCols(item.children) : [[], []];

              return (
                <li
                  key={item.label}
                  className="nav__item"
                  onMouseEnter={() => item.children && setOpenDropdown(item.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link
                    to={item.path}
                    className={`nav__link ${
                      location.pathname === item.path ? "nav__link--active" : ""
                    }`}
                  >
                    {item.label}
                    {item.children && <span className="nav__arrow">▼</span>}
                  </Link>

                  {item.children && openDropdown === item.label && (
                    <div className="mega">
                      <div className="mega__inner">
                        {mega && (
                          <>
                            <div className="mega__media">
                              <Link to={item.path} onClick={() => setOpenDropdown(null)}>
                                <img src={mega.image} alt={item.label} />
                                <span>{item.label}</span>
                              </Link>
                            </div>
                            <div className="mega__desc">
                              <p>{mega.desc}</p>
                              <Link
                                to={item.path}
                                className="mega__readmore"
                                onClick={() => setOpenDropdown(null)}
                              >
                                Read more
                              </Link>
                            </div>
                          </>
                        )}
                        <div className="mega__links">
                          <ul>
                            {col1.map((ch) => (
                              <li key={ch.label}>
                                <Link to={ch.path} onClick={() => setOpenDropdown(null)}>
                                  {ch.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                          {col2.length > 0 && (
                            <ul>
                              {col2.map((ch) => (
                                <li key={ch.label}>
                                  <Link to={ch.path} onClick={() => setOpenDropdown(null)}>
                                    {ch.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="mobile-nav">
          {NAV_ITEMS.map((item) => (
            <div key={item.label}>
              {item.children ? (
                <button
                  className="mobile-nav__btn"
                  onClick={() =>
                    setOpenDropdown(
                      openDropdown === item.label ? null : item.label
                    )
                  }
                >
                  {item.label}
                  <span>{openDropdown === item.label ? "▲" : "▼"}</span>
                </button>
              ) : (
                <Link
                  to={item.path}
                  className="mobile-nav__link"
                  onClick={closeMobile}
                >
                  {item.label}
                </Link>
              )}

              {item.children && openDropdown === item.label && (
                <div className="mobile-nav__sub">
                  {item.children.map((ch) => (
                    <Link
                      key={ch.label}
                      to={ch.path}
                      className="mobile-nav__sublink"
                      onClick={closeMobile}
                    >
                      {ch.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
};

export default Header;
